import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';
import { generatePath, useHistory, useRouteMatch } from 'react-router-dom';

import { useAppSelector, useResources } from '@/hooks';
import { flex, media } from '@/styles/theme';
import { NAVBAR_HEIGHT } from '@/config/variables';
import { SidebarResources } from '@/components/common';
import { ResourcePreview } from '@/components/agent/resources/preview/ResourcePreview';
import { selectAuthMenuType, selectCurrentClientId } from '@/store/selectors/authSelectors';
import { UserType } from '@/store/enums';
import routes from '@/config/routes';
import { selectPendingAction } from '@/store/selectors/resourcesSelectors';
import { getFavoritesList, getTree } from '@/store/slices/resourcesSlice';
import { ResourceLayoutSkeleton } from './ResourceLayout.skeleton';
import { LoaderWrapper } from '@/components/common/loaders/LoaderWrapper';

interface IProps {
  children: React.ReactChild;
}

const resourcesLayoutSelector = createSelector(
  selectCurrentClientId,
  selectAuthMenuType,
  selectPendingAction(getTree.typePrefix),
  selectPendingAction(getFavoritesList.typePrefix),
  (currentClientId, userType, isTreePending, isFavoritesPending) => ({
    currentClientId,
    userType,
    isPending: isTreePending || (isFavoritesPending && userType === UserType.agent),
  }),
);

const ResourcesLayout = ({ children }: IProps): React.ReactElement => {
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const { currentClientId, userType, isPending } = useAppSelector(resourcesLayoutSelector);
  const history = useHistory();
  const { initFolderTree } = useResources();
  const isIndexRoute = useRouteMatch({ path: routes.sp.scheduling.resources.index, exact: true });
  const isClientRoute = useRouteMatch<{ client: string }>({ path: routes.sp.scheduling.resources.client });

  useEffect(() => initFolderTree(userType), [initFolderTree, userType]);

  useEffect(() => {
    // handle missing client (can only show the one selected in the dropdown)
    if (
      userType === UserType.client &&
      currentClientId &&
      isClientRoute &&
      Number(isClientRoute.params.client) !== currentClientId
    ) {
      history.push(generatePath(routes.sp.scheduling.resources.client, { client: currentClientId }));
    }
  }, [currentClientId, history, isClientRoute, userType]);

  useEffect(() => {
    // handle missing home route for SU
    if (userType === UserType.client && currentClientId && isIndexRoute) {
      history.replace(generatePath(routes.sp.scheduling.resources.client, { client: currentClientId }));
    }
  }, [currentClientId, history, isIndexRoute, userType]);

  return (
    <MainContainer ref={mainContainerRef}>
      <SidebarResources />
      <RightPanel>
        <FullHeightLoaderWrapper isLoading={isPending} loader={<ResourceLayoutSkeleton />}>
          {children}
        </FullHeightLoaderWrapper>
      </RightPanel>
      <ResourcePreview />
    </MainContainer>
  );
};

export { ResourcesLayout };

const FullHeightLoaderWrapper = styled(LoaderWrapper)`
  height: 100%;
`;

const MainContainer = styled.div.attrs({
  className: `${flex.row} w-100`,
})`
  background-color: #fff;
`;

const RightPanel = styled.div.attrs({
  className: 'my-10 bg-white',
})`
  flex-grow: 1;

  /**
   * TODO: set explicit height of Top Navigation
   * currently 77px = *calculated* height of Top Navigation
   */
  height: calc(100vh - ${NAVBAR_HEIGHT}px);
  overflow: auto;

  padding: 48px 14px;

  ${media.tablet} {
    padding: 28px 34px;
  }
`;
